import { BuildType } from './ApiService';

interface FeatureAccessCheckResponse {
    hasAccess: boolean;
}

interface FeatureAccessRequestModel {
    id: string;
    userId: string;
    featureId: string;
    status: 'pending' | 'approved' | 'denied';
    formData: Record<string, unknown> | null;
    requestedAt: string;
}

export interface PendingFeatureAccessRequestAdminModel {
    id: string;
    userId: string;
    featureName: string;
    featureKey: string;
    formData: Record<string, unknown> | null;
    requestedAt: string;
    userName: string;
    userEmail: string;
    ageBracket: string;
}

export interface PaginatedPendingRequests {
    count: number;
    rows: PendingFeatureAccessRequestAdminModel[];
}

export interface ActiveFeatureAccessAdminModel {
    id: string;
    userId: string;
    userName: string;
    userEmail: string;
    featureName: string;
    featureKey: string;
    grantedAt: string;
    ageBracket: string;
}

export interface PaginatedActiveUsers {
    count: number;
    rows: ActiveFeatureAccessAdminModel[];
}

export interface DeniedFeatureAccessRequestAdminModel {
    id: string;
    userId: string;
    userName: string;
    userEmail: string;
    featureName: string;
    featureKey: string;
    formData: Record<string, unknown> | null;
    requestedAt: string;
}

export interface PaginatedDeniedRequests {
    count: number;
    rows: DeniedFeatureAccessRequestAdminModel[];
}

export interface FeatureSummary {
    id: string;
    key: string;
    name: string;
}

export interface FeatureAccessSummary {
    featureKey: string;
    featureName: string;
    usersWithAccess: number;
    usersWithoutAccess: number;
    totalUsers: number;
}

export interface UserWithoutAccessAdminModel {
    userId: string;
    userName: string;
    userEmail: string;
    ageBracket: string;
}

export interface PaginatedUsersWithoutAccess {
    count: number;
    rows: UserWithoutAccessAdminModel[];
}

export interface GrantAccessResult {
    granted: number;
    skipped: number;
    message: string;
}

export const featureAccessService = (build: BuildType) => ({
    checkFeatureAccess: build.query<FeatureAccessCheckResponse, string>({
        query: featureKey => `feature-access/check/${featureKey}`,
        providesTags: ['FeatureAccess'],
    }),
    getFeatureAccessRequestStatus: build.query<FeatureAccessRequestModel | null, string>({
        query: featureKey => `feature-access/status/${featureKey}`,
        providesTags: ['FeatureAccess'],
    }),
    submitFeatureAccessRequest: build.mutation<FeatureAccessRequestModel, { featureKey: string; formData?: Record<string, unknown> }>({
        query: body => ({
            url: 'feature-access/request',
            method: 'POST',
            body,
        }),
        invalidatesTags: ['FeatureAccess'],
    }),
    grantChatbotAccess: build.mutation<FeatureAccessCheckResponse, void>({
        query: () => ({
            url: 'feature-access/chatbot/access',
            method: 'POST',
        }),
        invalidatesTags: ['FeatureAccess'],
    }),
    getPendingFeatureAccessRequests: build.query<PaginatedPendingRequests, { page: number; limit: number }>({
        query: ({ page, limit }) => `feature-access/pending?page=${page}&limit=${limit}`,
        providesTags: ['FeatureAccess'],
    }),
    approveFeatureAccessRequests: build.mutation<{ message: string }, { requestIds: string[] }>({
        query: body => ({
            url: 'feature-access/approve-admin',
            method: 'POST',
            body,
        }),
        invalidatesTags: ['FeatureAccess'],
    }),
    revokeFeatureAccess: build.mutation<{ message: string }, { userId: string; featureKey: string }>({
        query: body => ({
            url: 'feature-access/revoke',
            method: 'POST',
            body,
        }),
        invalidatesTags: ['FeatureAccess'],
    }),
    getActiveFeatureAccessUsers: build.query<PaginatedActiveUsers, { featureKey: string; page: number; limit: number }>({
        query: ({ featureKey, page, limit }) => `feature-access/active-users?featureKey=${encodeURIComponent(featureKey)}&page=${page}&limit=${limit}`,
        providesTags: ['FeatureAccess'],
    }),
    getDeniedFeatureAccessRequests: build.query<PaginatedDeniedRequests, { featureKey: string; page: number; limit: number }>({
        query: ({ featureKey, page, limit }) => `feature-access/denied-requests?featureKey=${encodeURIComponent(featureKey)}&page=${page}&limit=${limit}`,
        providesTags: ['FeatureAccess'],
    }),
    getFeatures: build.query<FeatureSummary[], void>({
        query: () => 'feature-access/features',
        providesTags: ['FeatureAccess'],
    }),
    denyFeatureAccessRequests: build.mutation<{ message: string }, { requestIds: string[] }>({
        query: body => ({
            url: 'feature-access/deny-admin',
            method: 'POST',
            body,
        }),
        invalidatesTags: ['FeatureAccess'],
    }),
    getFeatureAccessSummary: build.query<FeatureAccessSummary, { featureKey: string }>({
        query: ({ featureKey }) => `feature-access/summary?featureKey=${encodeURIComponent(featureKey)}`,
        providesTags: ['FeatureAccess'],
    }),
    getUsersWithoutFeatureAccess: build.query<PaginatedUsersWithoutAccess, { featureKey: string; page: number; limit: number }>({
        query: ({ featureKey, page, limit }) => `feature-access/users-without-access?featureKey=${encodeURIComponent(featureKey)}&page=${page}&limit=${limit}`,
        providesTags: ['FeatureAccess'],
    }),
    grantFeatureAccess: build.mutation<GrantAccessResult, { featureKey: string; userIds?: string[]; all?: boolean }>({
        query: body => ({
            url: 'feature-access/grant',
            method: 'POST',
            body,
        }),
        invalidatesTags: ['FeatureAccess'],
    }),
});
